// src/pages/AdminPanel.jsx
import { useState, useEffect } from 'react';
import { api } from '../api';
import { useApp } from '../AppContext';
import { Card, PageHeader, Table, TR, TD, Badge, Btn, Modal, Flash, Spinner, Empty, Field, Input, Select } from '../components/UI';

export default function AdminPanel() {
  const { user } = useApp();
  const [users, setUsers]   = useState([]);
  const [roles, setRoles]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash]   = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [pwModal, setPwModal]   = useState(null);
  const [pinModal, setPinModal] = useState(null);
  const [newUser, setNewUser]   = useState({ username: '', password: '', role: 'employee' });
  const [newPw, setNewPw]       = useState('');
  const [newPin, setNewPin]     = useState({ pin: '', confirm: '' });
  const isSuperadmin = user?.role === 'superadmin';
  const perms = user?.permissions || {};

  function load() {
    setLoading(true);
    api.get('/api/admin/users').then(r => {
      if (r?.ok) { setUsers(r.data.users || []); setRoles(r.data.roles || []); }
      setLoading(false);
    });
  }
  useEffect(() => { load(); }, []);

  async function createUser() {
    const r = await api.post('/api/admin/create-user', newUser);
    if (r?.ok) { setFlash({ type: 'success', msg: `User "${newUser.username}" created.` }); setCreateModal(false); setNewUser({ username: '', password: '', role: 'employee' }); load(); }
    else setFlash({ type: 'error', msg: r?.data?.error || 'Failed to create user.' });
  }

  async function deleteUser(id, username) {
    if (!window.confirm(`Delete user "${username}"?`)) return;
    const r = await api.post(`/api/admin/delete-user/${id}`, {});
    if (r?.ok) { setUsers(prev => prev.filter(u => u.id !== id)); setFlash({ type: 'success', msg: `User "${username}" deleted.` }); }
  }

  async function updateRole(id, role) {
    const r = await api.post(`/api/admin/update-role/${id}`, { role });
    if (r?.ok) { setUsers(prev => prev.map(u => u.id === id ? { ...u, role } : u)); setFlash({ type: 'success', msg: 'Role updated.' }); }
  }

  async function resetPassword() {
    if (!newPw || newPw.length < 6) { setFlash({ type: 'error', msg: 'Password must be at least 6 characters.' }); return; }
    const r = await api.post(`/api/admin/reset-password/${pwModal.id}`, { password: newPw });
    if (r?.ok) { setFlash({ type: 'success', msg: 'Password updated.' }); setPwModal(null); setNewPw(''); }
  }

  async function setPin() {
    if (newPin.pin.length !== 4 || !newPin.pin.match(/^\d{4}$/)) { setFlash({ type: 'error', msg: 'PIN must be 4 digits.' }); return; }
    if (newPin.pin !== newPin.confirm) { setFlash({ type: 'error', msg: 'PINs do not match.' }); return; }
    const r = await api.post(`/api/admin/set-pin/${pinModal.id}`, { pin: newPin.pin });
    if (r?.ok) { setFlash({ type: 'success', msg: 'PIN set.' }); setPinModal(null); setNewPin({ pin: '', confirm: '' }); load(); }
    else setFlash({ type: 'error', msg: r?.data?.error || 'Failed.' });
  }

  async function clearPin(id) {
    if (!window.confirm('Remove this user\'s PIN?')) return;
    const r = await api.post(`/api/admin/clear-pin/${id}`, {});
    if (r?.ok) { setFlash({ type: 'success', msg: 'PIN removed.' }); load(); }
  }

  async function reset2fa(id) {
    const r = await api.post(`/admin/reset-2fa/${id}`, {});
    if (r?.ok) { setFlash({ type: 'success', msg: '2FA reset.' }); load(); }
  }

  const canManage = (u) => u.id !== user?.id && perms.can_manage_users && (isSuperadmin || u.role === 'employee');

  return (
    <div style={{ padding: '20px 16px', maxWidth: 1100, margin: '0 auto' }}>
      <PageHeader title="Admin Panel" actions={<Btn onClick={() => setCreateModal(true)}>Create User</Btn>} />
      {flash && <Flash type={flash.type} onDismiss={() => setFlash(null)}>{flash.msg}</Flash>}

      <Card>
        {loading ? <Spinner /> : users.length === 0 ? <Empty message="No users found." /> : (
          <Table headers={['Username', 'Role', '2FA', 'PIN', 'Actions']}>
            {users.map(u => (
              <TR key={u.id} highlight={u.id === user?.id}>
                <TD style={{ fontWeight: 'bold', color: 'var(--fg)' }}>{u.username}{u.id === user?.id ? <span style={{ fontSize: 10, color: 'var(--fg3)', marginLeft: 6 }}>(you)</span> : null}</TD>
                <TD>
                  {canManage(u) ? (
                    <select value={u.role} onChange={e => updateRole(u.id, e.target.value)}
                      style={{ padding: '4px 8px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--fg)', fontSize: 12, fontFamily: 'Arial, sans-serif' }}>
                      {roles.map(r => <option key={r.name} value={r.name}>{r.display}</option>)}
                      {isSuperadmin && <option value="superadmin">Super Admin</option>}
                    </select>
                  ) : <Badge variant="gray">{u.role}</Badge>}
                </TD>
                <TD>{u.totp_enabled ? <Badge variant="green">Enabled</Badge> : <Badge variant="gray">Off</Badge>}</TD>
                <TD>{u.pin_hash ? <Badge variant="blue">Set</Badge> : <Badge variant="gray">None</Badge>}</TD>
                <TD>
                  {canManage(u) && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <Btn variant="ghost" style={{ fontSize: 10, padding: '3px 7px' }} onClick={() => setPwModal(u)}>Reset Password</Btn>
                      {!['admin', 'superadmin'].includes(u.role) && (
                        <>
                          <Btn variant="ghost" style={{ fontSize: 10, padding: '3px 7px' }} onClick={() => setPinModal(u)}>{u.pin_hash ? 'Change PIN' : 'Set PIN'}</Btn>
                          {u.pin_hash && <Btn variant="ghost" style={{ fontSize: 10, padding: '3px 7px' }} onClick={() => clearPin(u.id)}>Remove PIN</Btn>}
                        </>
                      )}
                      {u.totp_enabled && isSuperadmin && <Btn variant="ghost" style={{ fontSize: 10, padding: '3px 7px' }} onClick={() => reset2fa(u.id)}>Reset 2FA</Btn>}
                      <Btn variant="danger" style={{ fontSize: 10, padding: '3px 7px' }} onClick={() => deleteUser(u.id, u.username)}>Delete</Btn>
                    </div>
                  )}
                </TD>
              </TR>
            ))}
          </Table>
        )}
      </Card>

      {createModal && (
        <Modal title="Create User" onClose={() => setCreateModal(false)}>
          <Field label="Username"><Input value={newUser.username} onChange={e => setNewUser(u => ({ ...u, username: e.target.value }))} /></Field>
          <Field label="Password" hint="Min 8 characters"><Input type="password" value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} /></Field>
          <Field label="Role">
            <Select value={newUser.role} onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}>
              {roles.map(r => <option key={r.name} value={r.name}>{r.display}</option>)}
              {isSuperadmin && <option value="superadmin">Super Admin</option>}
            </Select>
          </Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn onClick={createUser}>Create</Btn>
            <Btn variant="ghost" onClick={() => setCreateModal(false)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {pwModal && (
        <Modal title={`Reset Password — ${pwModal.username}`} onClose={() => setPwModal(null)}>
          <Field label="New Password" hint="Min 6 characters"><Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} /></Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn onClick={resetPassword}>Update Password</Btn>
            <Btn variant="ghost" onClick={() => setPwModal(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {pinModal && (
        <Modal title={`${pinModal.pin_hash ? 'Change' : 'Set'} PIN — ${pinModal.username}`} onClose={() => setPinModal(null)}>
          <Field label="New PIN (4 digits)"><Input type="password" maxLength={4} inputMode="numeric" value={newPin.pin} onChange={e => setNewPin(p => ({ ...p, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))} style={{ textAlign: 'center', letterSpacing: 8, fontSize: 20 }} /></Field>
          <Field label="Confirm PIN"><Input type="password" maxLength={4} inputMode="numeric" value={newPin.confirm} onChange={e => setNewPin(p => ({ ...p, confirm: e.target.value.replace(/\D/g, '').slice(0, 4) }))} style={{ textAlign: 'center', letterSpacing: 8, fontSize: 20 }} /></Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn onClick={setPin}>Set PIN</Btn>
            <Btn variant="ghost" onClick={() => setPinModal(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
